import React from "react";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { DateWithTooltip } from "components/DateWithTooltip";
import { WorkStatusChip } from "components/WorkStatusChip/WorkStatusChip";

import { useFetchGet } from "hooks/fetch/useFetchGet";

import { styled } from "@mui/material/styles";
import { StreamCard } from "./StreamCard";
import { User } from "interfaces/user";
import { WorkOrder } from "interfaces/work-order";

const useGetWorkOrderById = (
  workOrderId: number
): { loading: boolean; error: boolean; workOrder: WorkOrder | undefined } => {
  const { loading, error, response: workOrder } = useFetchGet(
    `work_orders/${workOrderId}`
  );

  return {
    loading,
    error,
    workOrder
  };
};

export interface WorkOrderCardProps {
  workOrderId: number;
  user: User;
}

export const WorkOrderCard = ({
  workOrderId,
  user
}: WorkOrderCardProps): JSX.Element => {
  const { loading, error, workOrder } = useGetWorkOrderById(workOrderId);

  if (loading || error || !workOrder)
    return <Skeleton variant="rectangular" width="100%" height={200} />;

  return (
    <StreamCard
      user={user}
      datetime={workOrder.create_date}
      label="Work Request"
      headerAdornment={<WorkStatusChip workOrder={workOrder.status} />}
      data-qa="CP-Card-Work-Order"
    >
      <ContentWrapper>
        <div>
          {workOrder.asset_detail?.name && (
            <>
              <BoldSpan>Asset Name: </BoldSpan>
              {workOrder.asset_detail.name}
              <br />
            </>
          )}
          <BoldSpan>Date Created: </BoldSpan>
          <DateWithTooltip date={workOrder.create_date} />
          <br />
          <BoldSpan>Requested Completion Date: </BoldSpan>
          <DateWithTooltip date={workOrder.requested_completion_date} />
          <br />
          <BoldSpan>Summary: </BoldSpan>
          {workOrder.summary}
          <br />
        </div>
        <MultilineParagraph>
          <BoldSpan>Problem Description: </BoldSpan>
          <br />
          {workOrder.problem_description}
        </MultilineParagraph>
        <LastUpdatedText variant="caption">
          <span>Last synchronised: </span>
          {workOrder.last_updated_externally && !workOrder.failed_sync_date ? (
            <DateWithTooltip
              date={workOrder.last_updated_externally}
              showTime
            />
          ) : workOrder?.failed_sync_date ? (
            <>
              <span>failed - ONYX notified - </span>
              <DateWithTooltip date={workOrder.failed_sync_date} showTime />
            </>
          ) : (
            <span>Awaiting Sync</span>
          )}
        </LastUpdatedText>
      </ContentWrapper>
    </StreamCard>
  );
};

const ContentWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BoldSpan = styled("span")`
  font-weight: bold;
`;

const MultilineParagraph = styled("p")`
  white-space: pre-wrap;
  margin: 0;
`;

const LastUpdatedText = styled(Typography)`
  display: flex;
  justify-content: flex-end;
  gap: 2px;
`;
